/**
 * Product catalog of one project (T-365): the records the project's extraction recipe made from its
 * documents. The filters and the open record live in the address, so a view can be shared.
 */

import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import FilterSelect from "../components/FilterSelect";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProductRecordReview } from "../components/catalog/ProductRecordReview";
import { useProject } from "../hooks/useProjects";
import { useProductRecords } from "../hooks/useProductRecords";
import { useWorkspaceUrl } from "../lib/routes";
import { useDateFormatter } from "../hooks/useDateFormatter";
import { cellText, columnKeys } from "../lib/productRecords";
import type { ProductRecordStatus } from "../services/naraDoor";

const STATUSES: ProductRecordStatus[] = ["needs_review", "approved", "outdated"];

export function ProductCatalog() {
  const { t } = useTranslation();
  const { id: projectId } = useParams<{ id: string }>();
  const urls = useWorkspaceUrl();
  const { formatDate } = useDateFormatter();
  const [params, setParams] = useSearchParams();
  const { data: project } = useProject(projectId);

  const status = STATUSES.find((s) => s === params.get("status"));
  const q = params.get("q") || undefined;
  const recordId = params.get("record") || undefined;
  const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useProductRecords(projectId, { status, q });

  // Ids and the error text only: the search text can be anything a person typed.
  useEffect(() => {
    if (isError) console.error("Failed to load product records", { projectId, error: (error as Error).message });
  }, [isError, error, projectId]);

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const columns = columnKeys(items);
  const labels = { yes: t("common.yes"), no: t("common.no") };

  const setParam = (key: string, value: string | undefined) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });

  return (
    <div>
      <PageHeader
        title={t("productCatalog.title")}
        breadcrumbs={[
          { label: t("nav.projects"), href: urls?.projects },
          { label: project?.name || "", href: urls && projectId ? urls.project(projectId) : undefined },
          { label: t("productCatalog.title") },
        ]}
      />

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <FilterSelect
          label={t("productCatalog.statusLabel")}
          value={status ?? ""}
          onChange={(value) => setParam("status", value)}
          allLabel={t("productCatalog.allStatuses")}
          options={STATUSES.map((s) => ({ value: s, label: t(`productCatalog.status.${s}`) }))}
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setParam("q", (new FormData(e.currentTarget).get("q") as string).trim());
          }}
        >
          <input
            type="search"
            name="q"
            key={q ?? ""}
            defaultValue={q}
            aria-label={t("common.search")}
            placeholder={t("productCatalog.searchPlaceholder")}
            className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background min-w-[240px]"
          />
        </form>
        {!isLoading && !isError && <span className="text-sm text-muted-foreground pb-2">{t("productCatalog.productCount", { count: items.length })}</span>}
      </div>

      {isLoading && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
      {isError && (
        <p role="alert" className="text-sm text-red-600">
          {t("productCatalog.errors.loadFailed")}
        </p>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted-foreground">{status || q ? t("productCatalog.noMatch") : t("productCatalog.empty")}</p>
      )}

      {items.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((key) => (
                <TableHead key={key}>{key}</TableHead>
              ))}
              <TableHead>{t("productCatalog.statusLabel")}</TableHead>
              <TableHead>{t("productCatalog.updated")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} className="cursor-pointer" onClick={() => setParam("record", item.id)}>
                {columns.map((key) => (
                  <TableCell key={key}>{cellText(item.data[key], labels)}</TableCell>
                ))}
                <TableCell>{t(`productCatalog.status.${item.status}`)}</TableCell>
                <TableCell>{formatDate(item.updated_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {hasNextPage && (
        <div className="mt-4">
          <Button variant="outline" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
            {isFetchingNextPage ? t("productCatalog.loadingMore") : t("productCatalog.loadMore")}
          </Button>
        </div>
      )}

      {recordId && projectId && <ProductRecordReview projectId={projectId} recordId={recordId} onClose={() => setParam("record", undefined)} />}
    </div>
  );
}
